import { cva, cx, type VariantProps } from 'class-variance-authority';

import Motion, { type MotionProps } from '../Motion';

type BackdropBlurAnchorX = 'left' | 'right';

type BackdropBlurAnchorY = 'top' | 'bottom';

type BackdropBlurVariant = 'card' | 'chiclet' | 'pill';

export interface BackdropBlurProps extends MotionProps {
  readonly anchor?: {
    x: BackdropBlurAnchorX;
    y: BackdropBlurAnchorY;
  };
  readonly className?: string | undefined;
  readonly color?: BackdropBlurColorVarProps;
  readonly size?: {
    height: number;
    width: number;
  };
  readonly variant: BackdropBlurVariant;
}

const DEFAULT_ANCHOR: BackdropBlurProps['anchor'] = {
  x: 'left',
  y: 'top',
};

const DEFAULT_COLOR: BackdropBlurColorVarProps = {
  sm: 'default/10',
  lg: 'default/10',
};

export type BackdropBlurColorVarProps = VariantProps<typeof backdropColorVar>;

const smColors = {
  'dark/10': 'bg-backdrop-500/10',
  'dark/40': 'bg-backdrop-500/40',
  'dark/70': 'bg-backdrop-500/70',
  'dark/100': 'bg-backdrop-500/100',
  'default/10': 'bg-sandstone-200/10',
  'default/40': 'bg-sandstone-200/40',
  'default/70': 'bg-sandstone-200/70',
  'default/100': 'bg-sandstone-200/100',
  'extra-dark/10': 'bg-backdrop-600/10',
  'extra-dark/40': 'bg-backdrop-600/40',
  'extra-dark/70': 'bg-backdrop-600/70',
  'extra-dark/100': 'bg-backdrop-600/100',
  'light/10': 'bg-white/10',
  'light/40': 'bg-white/40',
  'light/70': 'bg-white/70',
  'light/100': 'bg-white/100',
};

const lgColors = {
  'dark/10': 'lg:bg-backdrop-500/10',
  'dark/40': 'lg:bg-backdrop-500/40',
  'dark/70': 'lg:bg-backdrop-500/70',
  'dark/100': 'lg:bg-backdrop-500/100',
  'default/10': 'lg:bg-sandstone-200/10',
  'default/40': 'lg:bg-sandstone-200/40',
  'default/70': 'lg:bg-sandstone-200/70',
  'default/100': 'lg:bg-sandstone-200/100',
  'extra-dark/10': 'lg:bg-backdrop-600/10',
  'extra-dark/40': 'lg:bg-backdrop-600/40',
  'extra-dark/70': 'lg:bg-backdrop-600/70',
  'extra-dark/100': 'lg:bg-backdrop-600/100',
  'light/10': 'lg:bg-white/10',
  'light/40': 'lg:bg-white/40',
  'light/70': 'lg:bg-white/70',
  'light/100': 'lg:bg-white/100',
};

const backdropColorVar = cva('', {
  variants: {
    sm: smColors,
    lg: lgColors,
  },
  defaultVariants: {
    sm: 'default/10',
    lg: 'default/10',
  },
});

const getAnchorClasses = ({
  anchor,
}: Pick<BackdropBlurProps, 'anchor'>): Record<string, boolean> => ({
  'left-0': anchor!.x === 'left',
  'right-0': anchor!.x === 'right',
  'bottom-0': anchor!.y === 'bottom',
  'top-0': anchor!.y === 'top',
});

const getBorderRadiusClasses = ({
  variant,
}: Pick<BackdropBlurProps, 'variant'>): Record<string, boolean> => ({
  'rounded-xl': variant === 'card' || variant === 'chiclet',
  'rounded-3xl': variant === 'pill',
});

export const getAspectRatio = (
  size?: BackdropBlurProps['size'],
): string | undefined => {
  const hasSize = size !== undefined;
  return hasSize ? `${size.width}/${size.height}` : undefined;
};

const BackdropBlur = ({
  anchor = DEFAULT_ANCHOR,
  className,
  color = DEFAULT_COLOR,
  size,
  style,
  variant,
  ...props
}: BackdropBlurProps) => {
  return (
    <Motion
      {...props}
      aria-hidden="true"
      className={cx(
        'absolute min-h-px bg-blend-multiply backdrop-blur-xl',
        {
          ...getAnchorClasses({ anchor }),
          ...getBorderRadiusClasses({ variant }),
          'size-full': !size,
          'w-full': !!size,
        },
        backdropColorVar({ sm: color.sm, lg: color.lg, className }),
      )}
      data-cy="backdrop_blur"
      style={{
        ...style,
        aspectRatio: getAspectRatio(size),
      }}
    />
  );
};

export default BackdropBlur;
